import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Facebook, Linkedin, Instagram, Twitter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COAL_OPTIONS = [
    "Anthracite",
    "Bituminous — Coking",
    "Bituminous — Thermal",
    "Sub-Bituminous",
    "Lignite",
    "Pet Coke",
    "Indonesian Steam Coal",
    "South African RB1/RB2/RB3",
    "USA Coal",
    "Russian Coal",
    "Australian Coal",
    "Met Coke",
    "Other / Mixed",
];

const EMPTY = {
    name: "",
    company: "",
    email: "",
    phone: "",
    coal_type: "",
    quantity: "",
    destination_port: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill in name, email, phone and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success(
                "Enquiry received. Our trade desk will reach you within 24 hours."
            );
            setForm(EMPTY);
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Could not submit. Please call us directly.";
            toast.error(typeof msg === "string" ? msg : "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact">
            <section className="pt-40 pb-12 bg-coal-900 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-6">
                        Contact · Trade Desk
                    </div>
                    <h1 className="font-display font-bold text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92] text-white max-w-4xl">
                        Send your spec.
                        <br />
                        Get a quote in 24h.
                    </h1>
                </div>
            </section>

            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
                    {/* Form */}
                    <div className="lg:col-span-8 bg-coal-900 p-8 lg:p-12">
                        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white mb-2">
                            Enquiry Form
                        </h2>
                        <p className="text-zinc-400 text-sm mb-10">
                            Required fields are marked. The more spec you
                            share, the sharper our quote.
                        </p>
                        <form
                            onSubmit={handleSubmit}
                            data-testid="contact-form"
                            className="space-y-6"
                        >
                            <div className="grid sm:grid-cols-2 gap-6">
                                <Field
                                    label="Your Name *"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Full name"
                                    testid="contact-input-name"
                                />
                                <Field
                                    label="Company"
                                    name="company"
                                    value={form.company}
                                    onChange={handleChange}
                                    placeholder="Company / Plant"
                                    testid="contact-input-company"
                                />
                                <Field
                                    label="Email *"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@company.com"
                                    testid="contact-input-email"
                                />
                                <Field
                                    label="Phone *"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="+91 ..."
                                    testid="contact-input-phone"
                                />
                                <div>
                                    <label className="block text-xs font-medium text-zinc-400 uppercase tracking-[0.2em] mb-2">
                                        Coal Type
                                    </label>
                                    <select
                                        name="coal_type"
                                        value={form.coal_type}
                                        onChange={handleChange}
                                        data-testid="contact-input-coal-type"
                                        className="w-full bg-coal-700 border border-white/10 focus:border-ember focus:outline-none text-white py-3 px-4 font-sans"
                                    >
                                        <option value="">
                                            -- Select coal grade --
                                        </option>
                                        {COAL_OPTIONS.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <Field
                                    label="Quantity (MT / Month)"
                                    name="quantity"
                                    value={form.quantity}
                                    onChange={handleChange}
                                    placeholder="e.g. 25,000 MT / month"
                                    testid="contact-input-quantity"
                                />
                                <div className="sm:col-span-2">
                                    <Field
                                        label="Destination Port / Plant"
                                        name="destination_port"
                                        value={form.destination_port}
                                        onChange={handleChange}
                                        placeholder="e.g. Krishnapatnam — Sponge Iron Plant"
                                        testid="contact-input-destination"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-zinc-400 uppercase tracking-[0.2em] mb-2">
                                    Message / Spec Notes *
                                </label>
                                <textarea
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us your target GCV, ash, sulfur, sizing and delivery window."
                                    data-testid="contact-input-message"
                                    className="w-full bg-coal-700 border border-white/10 focus:border-ember focus:outline-none text-white py-3 px-4 font-sans resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-btn"
                                className="bg-ember hover:bg-ember-hover disabled:opacity-60 text-white px-8 py-4 font-bold tracking-[0.18em] uppercase text-sm transition-colors flex items-center gap-3"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Send Enquiry
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:col-span-4 bg-coal-800 p-8 lg:p-12">
                        <h3 className="font-display text-2xl font-semibold text-white mb-2">
                            Direct lines
                        </h3>
                        <p className="text-zinc-400 text-sm mb-8">
                            Prefer to speak directly? The trade desk picks up.
                        </p>

                        <div className="space-y-6">
                            <ContactRow
                                icon={Phone}
                                title="Phone"
                                lines={[
                                    {
                                        text: "+91 98722 27812",
                                        href: "tel:9872227812",
                                    },
                                    {
                                        text: "+91 87083 86260",
                                        href: "tel:8708386260",
                                    },
                                ]}
                            />
                            <ContactRow
                                icon={Mail}
                                title="Email"
                                lines={[
                                    {
                                        text: "carbexinternational@gmail.com",
                                        href: "mailto:carbexinternational@gmail.com",
                                    },
                                ]}
                            />
                            <ContactRow
                                icon={MapPin}
                                title="Office"
                                lines={[
                                    {
                                        text: "Carbex International Pvt Ltd.",
                                    },
                                    { text: "#6-C, Professor Colony" },
                                    { text: "Yamunanagar, Haryana — 135001" },
                                    { text: "India" },
                                ]}
                            />
                        </div>

                        <div className="mt-12 pt-8 border-t border-white/10">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold mb-3">
                                Hours
                            </div>
                            <div className="font-mono text-sm text-zinc-300">
                                Mon — Sat · 09:30 – 19:00 IST
                            </div>
                            <div className="font-mono text-sm text-zinc-500 mt-1">
                                Sun · Trade desk on call
                            </div>
                        </div>

                        <div className="mt-8 pt-8 border-t border-white/10">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold mb-3">
                                Connect
                            </div>
                            <div className="flex items-center gap-3">
                                <a
                                    href="https://www.facebook.com/share/1PnRj2FZsi/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Facebook"
                                    data-testid="contact-social-facebook"
                                    className="w-10 h-10 border border-white/15 hover:border-ember hover:bg-ember/10 text-zinc-300 hover:text-ember flex items-center justify-center transition-colors"
                                >
                                    <Facebook className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://www.linkedin.com/company/carbex-international-private-limited/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="LinkedIn"
                                    data-testid="contact-social-linkedin"
                                    className="w-10 h-10 border border-white/15 hover:border-ember hover:bg-ember/10 text-zinc-300 hover:text-ember flex items-center justify-center transition-colors"
                                >
                                    <Linkedin className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://x.com/carbexintpvtltd/status/2054885367514550749?s=20"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="X"
                                    data-testid="contact-social-x"
                                    className="w-10 h-10 border border-white/15 hover:border-ember hover:bg-ember/10 text-zinc-300 hover:text-ember flex items-center justify-center transition-colors"
                                >
                                    <Twitter className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://www.instagram.com/carbexinternational?utm_source=qr&igsh=MWYwOHF4am95dHVnMg=="
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Instagram"
                                    data-testid="contact-social-instagram"
                                    className="w-10 h-10 border border-white/15 hover:border-ember hover:bg-ember/10 text-zinc-300 hover:text-ember flex items-center justify-center transition-colors"
                                >
                                    <Instagram className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-zinc-400 uppercase tracking-[0.2em] mb-2">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-coal-700 border border-white/10 focus:border-ember focus:outline-none text-white py-3 px-4 placeholder:text-zinc-600 font-sans"
            />
        </div>
    );
}

function ContactRow({ icon: Icon, title, lines }) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-coal-900 border border-white/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-ember" />
            </div>
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                    {title}
                </div>
                <div className="space-y-1">
                    {lines.map((l, i) =>
                        l.href ? (
                            <a
                                key={i}
                                href={l.href}
                                className="block text-white hover:text-ember font-mono"
                            >
                                {l.text}
                            </a>
                        ) : (
                            <div key={i} className="text-zinc-300">
                                {l.text}
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
